import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  StyledErrorContainer,
  StyledErrorMessage,
  StyledErrorDetails,
  StyledRetryButton,
} from "./ErrorBoundary.styles";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <StyledErrorContainer>
          <StyledErrorMessage>
            <h1>Oops! Something went wrong</h1>
            <p>We're sorry, but an unexpected error has occurred.</p>
          </StyledErrorMessage>

          <StyledRetryButton onClick={this.handleRetry}>
            Try Again
          </StyledRetryButton>

          {import.meta.env.DEV && this.state.error && (
            <StyledErrorDetails>
              <h3>Error Details (Development Mode)</h3>
              <details>
                <summary>Error Message</summary>
                <pre>{this.state.error.message}</pre>
              </details>
              {this.state.errorInfo && (
                <details>
                  <summary>Component Stack</summary>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </details>
              )}
              {this.state.error.stack && (
                <details>
                  <summary>Stack Trace</summary>
                  <pre>{this.state.error.stack}</pre>
                </details>
              )}
            </StyledErrorDetails>
          )}
        </StyledErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
